import { useState } from 'react';
import { Panel, CountKpi } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { gbp, count, faintIfNull, NO_DATA } from '../lib/format.js';

interface Term {
  term: string; matchType: string | null; adGroup: string; campaign: string;
  firstSeen: string | null; lastSeen: string | null;
  impressions: number | null; clicks: number | null;
  costGbp: number | null; conversions: number | null; costPerClickGbp: number | null;
  wouldBeBlockedNow: boolean; blockedBy: string | null; blockedByList: string | null;
}

interface SearchTermsResponse {
  window: { from: string; to: string; days: number };
  totals: {
    termsSeen: number; termsCosting: number; costGbp: number | null;
    clicks: number; conversions: number | null; costWithoutConversionGbp: number | null;
  };
  exclusions: { total: number; byList: { list: string; count: number }[] };
  costNowExcludedGbp: number | null;
  costing: Term[];
  shownOnly: Term[];
  generatedUtc: string;
}

const ALL = '__all__';

export function SearchTerms({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error } = useApi<SearchTermsResponse>('/api/search-terms?days=30', getToken);
  const [campaign, setCampaign] = useState(ALL);

  if (error) return <EmptyState title="Could not load search terms" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading what people actually searched for.</EmptyState>;

  const campaigns = Array.from(new Set([...data.costing, ...data.shownOnly].map((t) => t.campaign))).sort();
  const filter = (rows: Term[]) => rows.filter((t) => campaign === ALL || t.campaign === campaign);
  const costing = filter(data.costing);
  const shownOnly = filter(data.shownOnly);

  if (data.totals.termsSeen === 0) {
    return (
      <EmptyState title="No search terms recorded yet" tag="No data">
        Search terms appear once the campaigns have served. Nothing has been searched, or the
        window {data.window.from} to {data.window.to} is empty.
      </EmptyState>
    );
  }

  return (
    <div className="stack">
      <Panel
        title="What the money bought"
        subtitle={`Terms people actually typed, ${data.window.from} to ${data.window.to}. Ordered by cost, because the expensive term matters more than the frequent one.`}
      >
        <div className="kpi-grid">
          <CountKpi label="Terms seen" value={data.totals.termsSeen} note={`${data.totals.termsCosting} cost money`} />
          <CountKpi label="Spend on search terms" value={data.totals.costGbp} money />
          <CountKpi label="Clicks" value={data.totals.clicks || null} />
          <CountKpi
            label="Spent without a conversion"
            value={data.totals.costWithoutConversionGbp}
            money
            note="Not necessarily waste — but it is where to look"
          />
          <CountKpi
            label="Active exclusions"
            value={data.exclusions.total || null}
            note={data.exclusions.byList.map((b) => `${b.list}: ${b.count}`).join(' · ')}
          />
          <CountKpi
            label="Spend now excluded"
            value={data.costNowExcludedGbp}
            money
            note="Already spent, on terms today's exclusions would stop"
          />
        </div>
      </Panel>

      {campaigns.length > 1 ? (
        <div className="panel">
          <select className="select" value={campaign} onChange={(e) => setCampaign(e.target.value)}>
            <option value={ALL}>All campaigns</option>
            {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      ) : null}

      <Panel
        title={`Terms that cost money (${costing.length})`}
        subtitle="A term here took a click and charged for it. 'Now excluded' means today's negative keywords would stop it — not that it was stopped when the money was spent."
      >
        <table className="data">
          <thead>
            <tr>
              <th>Search term</th>
              <th>Ad group</th>
              <th>Match</th>
              <th className="num">Impr.</th>
              <th className="num">Clicks</th>
              <th className="num">Cost</th>
              <th className="num">CPC</th>
              <th className="num">Conv.</th>
              <th>Now excluded</th>
            </tr>
          </thead>
          <tbody>
            {costing.map((t) => (
              <tr key={`${t.campaign}|${t.adGroup}|${t.term}`}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{t.term}</td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{t.adGroup}</td>
                <td>{t.matchType ? <span className="pill">{t.matchType}</span> : <span style={{ color: 'var(--text-faint)' }}>—</span>}</td>
                <td className="num" style={faintIfNull(t.impressions)}>{count(t.impressions)}</td>
                <td className="num" style={faintIfNull(t.clicks)}>{count(t.clicks)}</td>
                <td className="num" style={{ fontWeight: 500 }}>{gbp(t.costGbp)}</td>
                <td className="num" style={faintIfNull(t.costPerClickGbp)}>{gbp(t.costPerClickGbp)}</td>
                <td
                  className="num"
                  style={{ color: (t.conversions ?? 0) === 0 ? 'var(--text-faint)' : 'var(--positive)' }}
                >
                  {count(t.conversions)}
                </td>
                <td>
                  {t.wouldBeBlockedNow ? (
                    <span className="pill good" title={`Matched by "${t.blockedBy}"${t.blockedByList ? ` in ${t.blockedByList}` : ''}`}>
                      Yes
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-faint)', fontSize: 12 }}>No</span>
                  )}
                </td>
              </tr>
            ))}
            {costing.length === 0 ? (
              <tr><td colSpan={9} style={{ color: 'var(--text-faint)' }}>No term has cost money in this window.</td></tr>
            ) : null}
          </tbody>
        </table>
      </Panel>

      <Panel
        title={`Shown but never clicked (${shownOnly.length})`}
        subtitle="These have cost nothing so far. Excluding an unsuitable one here is the only negative keyword that is free — after a click, the money has already gone."
      >
        <table className="data">
          <thead>
            <tr>
              <th>Search term</th>
              <th>Ad group</th>
              <th className="num">Impressions</th>
              <th>First seen</th>
            </tr>
          </thead>
          <tbody>
            {shownOnly.slice(0, 200).map((t) => (
              <tr key={`${t.campaign}|${t.adGroup}|${t.term}`}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{t.term}</td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{t.adGroup}</td>
                <td className="num" style={faintIfNull(t.impressions)}>{count(t.impressions)}</td>
                <td style={{ color: 'var(--text-faint)', fontSize: 12 }}>{t.firstSeen ?? NO_DATA}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shownOnly.length > 200 ? (
          <p style={{ marginTop: 12, fontSize: 12, color: 'var(--text-faint)' }}>
            Showing the first 200 of {count(shownOnly.length)}.
          </p>
        ) : null}
      </Panel>

      <div className="panel">
        <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: 0 }}>
          Adding a negative keyword changes a live advertising account and requires approval. Nothing
          here is excluded automatically. "Now excluded" approximates Google's matching rather than
          reproducing it, so treat it as a strong indication and not a guarantee.
        </p>
      </div>
    </div>
  );
}
